import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    SimpleGrid,
    Text,
    Select,
    Button,
    useToast,
    Skeleton,
    useBreakpointValue,
    Heading,
    FormControl,
    FormLabel,
    useColorModeValue,
    Flex,
    Center,
    Spinner,
} from '@chakra-ui/react';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    LineChart,
    Line,
    Cell,
} from 'recharts';
import { DataKey } from 'recharts/types/util/types';
import api from '../auth/apiClient';


// Custom trendline calculation using least squares regression
const calculateTrendline = (data: { x: number; y: number }[]): { slope: number; intercept: number } => {
    const n = data.length;
    if (n < 2) return { slope: 0, intercept: 0 };

    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumXX = 0;

    data.forEach(point => {
        sumX += point.x;
        sumY += point.y;
        sumXY += point.x * point.y;
        sumXX += point.x * point.x;
    });

    const denominator = (n * sumXX - sumX * sumX);

    // Avoid division by zero or invalid calculations
    if (denominator === 0 || !isFinite(denominator)) {
        return { slope: 0, intercept: sumY / n };
    }

    const slope = (n * sumXY - sumX * sumY) / denominator;
    const intercept = (sumY - slope * sumX) / n;

    // Check if results are valid numbers
    if (!isFinite(slope) || !isFinite(intercept)) {
        return { slope: 0, intercept: sumY / n };
    }

    return { slope, intercept };
};

const getTrendValue = (x: number, slope: number, intercept: number): number => {
    const value = slope * x + intercept;
    // Ensure we return a valid number and never negative
    if (!isFinite(value)) return 0;
    return Math.max(0, value); // Never return negative values
};

export interface ProductionData {
    periode: string;
    estate: string;
    division: string;
    block: string;
    block_status: string;
    planted_year: string;
    planted_area: number;
    total_planted: number;
    sph: number;
    sph_prc: number;
    harvest_area: number;
    rotation: number;
    hk_act: number;
    hk_bgt: number;
    hk_prc: number;
    jjg_act: number;
    jjg_bgt: number;
    jjg_prc: number;
    weight_act: number;
    weight_bgt: number;
    weight_prc: number;
    yield_act: number;
    yield_bgt: number;
    yield_prc: number;
    bjr_act: number;
    bjr_bgt: number;
    bjr_prc: number;
    brd_qty_act: number;
    brd_prc: number;
    lebih_basis_prc: number;
    hk_cost: number;
    premi_lebih_basis: number;
    premi_brd: number;
    total_cost: number;
    hk_cost_per_kg: number;
}

interface FilterState {
    tahun: string;
    bulan: string;
    jenis_laporan: string;
    estate?: string;
    company_id?: number;
    estate_id?: number;
}

interface AggPoint {
    division: string;
    avgOutput: number;
    avgCost: number;
}

interface Point {
    division?: string;
    output: number;
    hk_cost_per_kg: number;
}

interface ApiResponse {
    data: ProductionData[];
}

export interface YieldBjrData {
    estate: string;
    operating_unit_id: number;
    company_id: number;
    estate_id: number;
    division_id: number;
    block_id: number;
    planted_area: number;
    plant_total: number;
    planted_year: string;
    fiscal_year: string;
    weight: number;
    bunch_qty: number;
    yield: number;
}

interface YieldBjrApiResponse {
    status: string;
    data: YieldBjrData[];
    count: number;
}

export interface HarvestAttendanceData {
    start_date: string;
    end_date: string;
    estate: string;
    total_pemanen: number;
    total_fp: number;
}

interface HarvestAttendanceApiResponse {
    status: string;
    data: HarvestAttendanceData[];
    count: number;
}

// ubah signature dan tambahkan guard di formatPeriode
export const formatPeriode = (periode: string = ''): string => {
    if (periode.length < 6) return '';    // guard: pasti ada 6 char "YYYYMM"
    const month = parseInt(periode.slice(4, 6), 10);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
    return monthNames[month - 1] || '';
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28DFF', '#FF6666'];
const COLORS2 = ['#A28DFF', '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#FF6666'];

const _ACTUAL = '#2F80ED'
const _TARGET = '#F2994A'
const _AGG_ACTUAL = '#27AE60'
const _AGG_TARGET = '#F24C4C'

export const formatNumber = (value: number, type: 'number' | 'percent' | 'currency' = 'number'): string => {
    if (type === 'percent') {
        return `${(value * 100).toFixed(2)}%`;
    }
    if (type === 'currency') {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    }
    if (type === 'number') {
        return new Intl.NumberFormat('id-ID', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    }
    return new Intl.NumberFormat('id-ID').format(value);
};

export const formatNumber2Digits = (value: number, type: 'number' | 'percent' | 'currency' = 'number'): string => {
    if (type === 'percent') {
        return `${(value * 100).toFixed(2)}%`;
    }
    if (type === 'currency') {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    }
    if (type === 'number') {
        return new Intl.NumberFormat('id-ID', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    }
    return new Intl.NumberFormat('id-ID').format(value);
};

// Trend Produksi Bulanan
export const CustomizedAxisTick = (props: any) => {
    const { x, y, payload } = props;

    // Split text jika terlalu panjang
    const MAX_CHARS = 150;
    const text = payload.value;
    const textParts = [];

    for (let i = 0; i < text.length; i += MAX_CHARS) {
        textParts.push(text.substring(i, i + MAX_CHARS));
    }

    return (
        <g transform={`translate(${x},${y})`}>
            {textParts.map((part, index) => (
                <text
                    key={index}
                    x={60}
                    y={index * 15} // Jarak antar baris
                    dy={16}
                    textAnchor="end"
                    fill="#666"
                    transform="rotate(0)"
                    fontSize={10} // Ukuran font lebih kecil
                    fontFamily="sans-serif"
                >
                    {part}
                </text>
            ))}
        </g>
    );
};

export const CustomTooltipTon = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        let aktual = 0;
        let budget = 0;

        payload.forEach((item: any) => {
            if (item.name === 'Aktual' || item.dataKey === 'aktual') aktual = item.value;
            if (item.name === 'Budget' || item.dataKey === 'budget') budget = item.value;
        });

        let percentage = 0;
        if (budget > 0) {
            percentage = (aktual / budget) * 100;
        }

        return (
            <Box
                bg="white"
                p={3}
                borderRadius="md"
                boxShadow="lg"
                borderWidth="1px"
                maxWidth="300px"
            >
                <Text fontWeight="bold" mb={1}>Estate: {label}</Text>
                {payload.map((item: any, index: number) => (
                    <Text key={index} color={item.color} fontSize="sm">
                        {item.name}: {formatNumber(item.value, 'number')} ton
                    </Text>
                ))}
                {budget > 0 && (
                    <Text
                        color={percentage >= 100 ? "green.600" : "orange.600"}
                        fontSize="sm"
                        fontWeight="bold"
                    >
                        Persentase: {percentage.toFixed(1)}%
                    </Text>
                )}
            </Box>
        );
    }
    return null;
};

// Custom label component to render values on top of bars
const CustomBarLabel = (props: any) => {
    const { x, y, width, value } = props;
    return (
        <text
            x={x + width / 2}
            y={y - 5}
            fill="#666"
            textAnchor="middle"
            fontSize={12}
            fontWeight="600"
        >
            {formatNumber(value, 'number')} ton
        </text>
    );
};

// Custom tooltip for YTD and MTD bar charts
const CustomTooltipBar = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <Box
                bg="white"
                p={3}
                borderRadius="md"
                boxShadow="lg"
                borderWidth="1px"
                maxWidth="300px"
            >
                <Text fontWeight="bold" mb={1}>{label}</Text>
                {payload.map((item: any, index: number) => (
                    <Text key={index} fontSize="sm">
                        <Text as="span" color={item.payload.fill || item.color}>
                            Berat: {formatNumber(item.value, 'number')} ton
                        </Text>
                    </Text>
                ))}
            </Box>
        );
    }
    return null;
};

// Custom tooltip for Monthly Trend line chart
const CustomTooltipMonthly = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        let aktual = 0;
        let target = 0;

        payload.forEach((item: any) => {
            if (!item.dataKey.endsWith('_trend')) {
                if (item.name === 'Aktual' || item.dataKey === 'aktual') aktual = item.value;
                if (item.name === 'Target' || item.dataKey === 'target') target = item.value;
            }
        });

        let percentage = 0;
        if (target > 0) {
            percentage = (aktual / target) * 100;
        }

        return (
            <Box
                bg="white"
                p={3}
                borderRadius="md"
                boxShadow="lg"
                borderWidth="1px"
                maxWidth="300px"
            >
                <Text fontWeight="bold" mb={1}>{label}</Text>
                {payload
                    .filter((item: any) => !item.dataKey.endsWith('_trend')) // Filter out trend lines
                    .map((item: any, index: number) => (
                        <Text key={index} color={item.color} fontSize="sm">
                            {item.name}: {formatNumber(item.value, 'number')} ton
                        </Text>
                    ))
                }
                {target > 0 && (
                    <Text
                        color={percentage >= 100 ? "green.600" : "orange.600"}
                        fontSize="sm"
                        fontWeight="bold"
                    >
                        Persentase: {percentage.toFixed(1)}%
                    </Text>
                )}
            </Box>
        );
    }
    return null;
};

// Custom tooltip for Yield chart
const CustomTooltipYield = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <Box
                bg="white"
                p={3}
                borderRadius="md"
                boxShadow="lg"
                borderWidth="1px"
                maxWidth="300px"
            >
                <Text fontWeight="bold" mb={1}>{label}</Text>
                {payload
                    .filter((item: any) => !item.dataKey.endsWith('_trend')) // Filter out trend lines
                    .map((item: any, index: number) => (
                        <Text key={index} color={item.color} fontSize="sm">
                            {item.name}: {formatNumber2Digits(item.value, 'number')} Ton
                        </Text>
                    ))
                }
            </Box>
        );
    }
    return null;
};

// Custom tooltip for BJR chart
const CustomTooltipBjr = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <Box
                bg="white"
                p={3}
                borderRadius="md"
                boxShadow="lg"
                borderWidth="1px"
                maxWidth="300px"
            >
                <Text fontWeight="bold" mb={1}>{label}</Text>
                {payload
                    .filter((item: any) => !item.dataKey.endsWith('_trend')) // Filter out trend lines
                    .map((item: any, index: number) => (
                        <Text key={index} color={item.color} fontSize="sm">
                            {item.name}: {formatNumber2Digits(item.value, 'number')} kg
                        </Text>
                    ))
                }
            </Box>
        );
    }
    return null;
};

const calculateDynamicDomain = (minValue: number): number => {
    // Round down to nearest 10
    return Math.floor(minValue / 10) * 10;
};


const EstateDashboardComponent = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = (new Date().getMonth() + 1).toString().padStart(2, '0');
    const [data, setData] = useState<ProductionData[]>([]);
    const [dataFiltered, setDataFiltered] = useState<ProductionData[]>([]);
    const [filters, setFilters] = useState<FilterState>({
        tahun: currentYear.toString(),
        bulan: currentMonth,
        jenis_laporan: "aktual_vs_target_ytd",
        company_id: 1,
    });
    const [appliedFilters, setAppliedFilters] = useState<FilterState>({
        tahun: currentYear.toString(),
        bulan: currentMonth,
        jenis_laporan: "aktual_vs_target_ytd",
        company_id: 1,
    });
    const [loading, setLoading] = useState(true);
    const [weeklyProductionData, setWeeklyProductionData] = useState<any[]>([]);
    const [weeklyLoading, setWeeklyLoading] = useState(false);
    const [yieldBjrData, setYieldBjrData] = useState<YieldBjrData[]>([]);
    const [yieldBjrLoading, setYieldBjrLoading] = useState(false);
    const [harvestAttendanceData, setHarvestAttendanceData] = useState<HarvestAttendanceData[]>([]);
    const [harvestAttendanceLoading, setHarvestAttendanceLoading] = useState(false);
    const [hoveringDataKey, setHoveringDataKey] = React.useState<DataKey<any> | undefined>(undefined);
    const toast = useToast();

    const [companies, setCompanies] = useState<any[]>([]);
    const [estates, setEstates] = useState<any[]>([]);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [optionsResponse, estatesDbResponse] = await Promise.all([
                    api.get('/inspeksi/options'),
                    api.get('/inspeksi/web/estate-db'),
                ]);

                const data = optionsResponse.data;
                const estatesDbData = estatesDbResponse.data;

                setCompanies(data.companies || []);

                const mergedEstates = (estatesDbData || []).map((estateDb: any) => {
                    const optionsEstate = (data.estates || []).find((est: any) => est.name === estateDb.name);
                    return {
                        id: estateDb.id,
                        name: estateDb.name,
                        company_id: estateDb.company_id,
                        odoo_id: optionsEstate?.odoo_id || estateDb.id,
                    };
                });
                setEstates(mergedEstates);
            } catch (error) {
                console.error('Error fetching options:', error);
            }
        };
        fetchOptions();
    }, []);

    const filteredEstates = useMemo(() => {
        if (!filters.company_id) return estates;
        return estates.filter(e => e.company_id === filters.company_id);
    }, [estates, filters.company_id]);

    const ESTATE_OPTIONS = [
        "LME",
        "MME",
        "PAM1",
        "PAM2"
    ];

    const handleMouseEnter = (payload: any) => {
        setHoveringDataKey(payload.dataKey);
    };

    const handleMouseLeave = () => {
        setHoveringDataKey(undefined);
    };

    const fetchData = async () => {
        try {
            setLoading(true); // Reset loading state setiap kali fetch

            const selectedYear = Number.parseInt(filters.tahun);
            const tahun_bulan = `${filters.tahun}${filters.bulan}`;

            let startPeriod: string, endPeriod: string;
            if (filters.jenis_laporan === "aktual_vs_target_ytd") {
                startPeriod = `${selectedYear}01`;
                endPeriod = tahun_bulan;
            } else {
                startPeriod = tahun_bulan;
                endPeriod = tahun_bulan;
            }

            const companyParam = filters.company_id ? `&company_id=${filters.company_id}` : '';
            const estateParam = filters.estate_id ? `&estate_id=${filters.estate_id}` : (filters.estate ? `&estate=${filters.estate}` : '');
            const response = await api.get(`/odoo/api/report-produksi/all?start_period=${startPeriod}&end_period=${endPeriod}${companyParam}${estateParam}`);
            // console.log('Fetched data:', response.data);

            if (response.data && response.data.data) {
                setData(response.data.data);

                const apiResult = response.data as ApiResponse;
                setDataFiltered(
                    apiResult.data.filter((item: ProductionData) => item.periode <= tahun_bulan)
                );
            } else {
                setData([]);
                setDataFiltered([]);
            }
        } catch (err) {
            toast({
                title: 'Error',
                description: 'Terjadi kesalahan saat memuat data',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch weekly production data
    const fetchWeeklyProductionData = async () => {
        try {
            setWeeklyLoading(true);
            const periode = `${filters.tahun}${filters.bulan}`;
            const companyParam = filters.company_id ? `&company_id=${filters.company_id}` : '';
            const response = await api.get(`/api/report-estate-weekly?periode=${periode}${companyParam}`);
            if (response.data?.status === "success" && response.data?.data) {
                setWeeklyProductionData(response.data.data);
            } else {
                setWeeklyProductionData([]);
            }
        } catch (err) {
            toast({
                title: 'Error',
                description: 'Gagal memuat data produksi mingguan',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        } finally {
            setWeeklyLoading(false);
        }
    };

    // Fetch yield data
    const fetchYieldData = async () => {
        try {
            setYieldBjrLoading(true);
            const selectedYear = filters.tahun;

            const estateParam = filters.estate_id ? `&estate_id=${filters.estate_id}` : '';
            const response = await api.get<YieldBjrApiResponse>(
                `/api/report-yield?fiscal_year=${selectedYear}&company_id=${filters.company_id || 1}${estateParam}`
            );

            if (response.data?.status === "success" && response.data?.data) {
                setYieldBjrData(response.data.data);
            } else {
                setYieldBjrData([]);
            }
        } catch (err) {
            toast({
                title: 'Error',
                description: 'Gagal memuat data yield',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        } finally {
            setYieldBjrLoading(false);
        }
    };

    // Fetch harvest attendance data
    const fetchHarvestAttendanceData = async () => {
        try {
            setHarvestAttendanceLoading(true);
            const periode = `${filters.tahun}${filters.bulan}`;
            const companyParam = filters.company_id ? `&company_id=${filters.company_id}` : '';
            const response = await api.get<HarvestAttendanceApiResponse>(
                `/api/report-harvest-attendance?periode=${periode}${companyParam}`
            );

            if (response.data?.status === "success" && response.data?.data) {
                setHarvestAttendanceData(response.data.data);
            } else {
                setHarvestAttendanceData([]);
            }
        } catch (err) {
            toast({
                title: 'Error',
                description: 'Gagal memuat data kehadiran pemanen',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        } finally {
            setHarvestAttendanceLoading(false);
        }
    };

    const handleRefresh = async () => {
        setAppliedFilters({ ...filters });
        await Promise.all([
            fetchData(),
            fetchWeeklyProductionData(),
            fetchYieldData(),
            fetchHarvestAttendanceData()
        ]);
    };

    useEffect(() => {
        const tahun_bulan = `${appliedFilters.tahun}${appliedFilters.bulan}`;
        if (data && Array.isArray(data)) {
            setDataFiltered(
                data.filter(item => item.periode <= tahun_bulan)
            );
        } else {
            setDataFiltered([]);
        }
    }, [data, appliedFilters.tahun, appliedFilters.bulan]);

    // Initial load
    useEffect(() => {
        handleRefresh();
    }, []);

    const processedData = useMemo(() => {
        if (!data || data.length === 0) return {};

        return data.reduce((acc: Record<string, ProductionData>, item) => {
            const key = item.estate;
            if (!acc[key]) {
                acc[key] = { ...item };
            } else {
                acc[key].weight_act += item.weight_act;
                acc[key].weight_bgt += item.weight_bgt;
            }
            return acc;
        }, {});
    }, [data]);

    const estateData = useMemo(() => Object.values(processedData), [processedData]);

    const costData = useMemo(() => {
        if (!data || data.length === 0) return [];

        return [
            { name: 'Biaya HK', value: data.reduce((sum, item) => sum + item.hk_cost, 0) },
            { name: 'Premi Lebih Basis', value: data.reduce((sum, item) => sum + item.premi_lebih_basis, 0) },
            { name: 'Premi Brondolan', value: data.reduce((sum, item) => sum + item.premi_brd, 0) },
        ];
    }, [data]);

    // Process weekly production data for the chart
    const weeklyChartData = useMemo(() => {
        if (!weeklyProductionData || weeklyProductionData.length === 0) return [];

        // Filter by estate if selected
        const estateFilteredData = appliedFilters.estate
            ? weeklyProductionData.filter(item => item.supplier === appliedFilters.estate)
            : weeklyProductionData;

        // Filter hanya data yang ada supplier dan source
        const filteredData = estateFilteredData.filter(
            item => item.supplier && item.source
        );

        // Group data by label_date
        const groupedByDate = filteredData.reduce((result: Record<string, any[]>, item) => {
            if (!result[item.label_date]) {
                result[item.label_date] = [];
            }
            result[item.label_date].push(item);
            return result;
        }, {});

        // Convert to format needed by chart
        const dates = Object.keys(groupedByDate).sort((a, b) => {
            const dateA = groupedByDate[a][0].start_date;
            const dateB = groupedByDate[b][0].start_date;
            return new Date(dateA).getTime() - new Date(dateB).getTime();
        });

        // Create a map of all suppliers to ensure all data points have values
        const allSuppliers = new Set<string>();
        filteredData.forEach(item => {
            if (item.supplier) allSuppliers.add(item.supplier);
        });

        // Transform data for the chart
        const transformedData = dates.map(date => {
            const dateItems = groupedByDate[date];
            const result: Record<string, any> = { label_date: date };

            // Initialize all suppliers with 0 values
            Array.from(allSuppliers).forEach(supplier => {
                result[supplier] = 0;
            });

            // Add actual values
            dateItems.forEach(item => {
                if (item.supplier) {
                    result[item.supplier] = item.net_weight;
                }
            });

            return result;
        });

        // Calculate trendlines for each supplier
        if (transformedData.length >= 2) {
            const suppliersArray = Array.from(allSuppliers);
            suppliersArray.forEach(supplier => {
                try {
                    const points = transformedData
                        .map((d, i) => ({ x: i, y: d[supplier] || 0 }))
                        .filter(p => typeof p.y === 'number' && !isNaN(p.y) && isFinite(p.y));

                    if (points.length >= 2) {
                        const { slope, intercept } = calculateTrendline(points);

                        // Only add trendline if values are valid
                        if (isFinite(slope) && isFinite(intercept)) {
                            transformedData.forEach((d, i) => {
                                const trendValue = getTrendValue(i, slope, intercept);
                                if (isFinite(trendValue)) {
                                    d[`${supplier}_trend`] = trendValue;
                                }
                            });
                        }
                    }
                } catch (error) {
                    console.warn(`Error calculating trendline for ${supplier}:`, error);
                }
            });
        }

        return transformedData;
    }, [weeklyProductionData, appliedFilters.estate]);


    // Yield Chart Data - Group by fiscal_year and calculate yield (weight/planted_area) per estate
    const yieldChartData = useMemo(() => {
        if (!yieldBjrData || yieldBjrData.length === 0) return [];

        const source = (appliedFilters.estate && !appliedFilters.estate_id)
            ? yieldBjrData.filter(item => item.estate === appliedFilters.estate)
            : yieldBjrData;

        // Group by fiscal_year and estate, then calculate yield as weight/planted_area
        const grouped: Record<string, Record<string, { total_weight: number; total_planted_area: number; count: number }>> = {};

        source.forEach(row => {
            const year = row.fiscal_year;
            if (!grouped[year]) grouped[year] = {};
            if (!grouped[year][row.estate]) {
                grouped[year][row.estate] = { total_weight: 0, total_planted_area: 0, count: 0 };
            }
            grouped[year][row.estate].total_weight += row.weight;
            grouped[year][row.estate].total_planted_area += row.planted_area;
            grouped[year][row.estate].count += 1;
        });

        // Get all unique estates and sort them in consistent order
        const allEstates = new Set<string>();
        source.forEach(item => allEstates.add(item.estate));

        // Define estate order for consistent legend ordering
        const estateOrder = ['LME', 'MME', 'PAM1', 'PAM2'];
        const sortedEstates = Array.from(allEstates).sort((a, b) => {
            const indexA = estateOrder.indexOf(a);
            const indexB = estateOrder.indexOf(b);
            if (indexA === -1 && indexB === -1) return a.localeCompare(b);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });

        // Sort years
        const sortedYears = Object.keys(grouped).sort();

        // Transform to chart format
        const transformedData = sortedYears.map(year => {
            const result: Record<string, any> = { periode: year };
            sortedEstates.forEach(estate => {
                const estateData = grouped[year][estate];
                result[estate] = estateData && estateData.total_planted_area > 0
                    ? estateData.total_weight / estateData.total_planted_area
                    : 0;
            });
            return result;
        });

        // Calculate trendlines for each estate
        if (transformedData.length >= 2) {
            sortedEstates.forEach(estate => {
                try {
                    const points = transformedData
                        .map((d, i) => ({ x: i, y: d[estate] || 0 }))
                        .filter(p => typeof p.y === 'number' && !isNaN(p.y) && isFinite(p.y));

                    if (points.length >= 2) {
                        const { slope, intercept } = calculateTrendline(points);

                        // Only add trendline if values are valid
                        if (isFinite(slope) && isFinite(intercept)) {
                            transformedData.forEach((d, i) => {
                                const trendValue = getTrendValue(i, slope, intercept);
                                if (isFinite(trendValue)) {
                                    d[`${estate}_trend`] = trendValue;
                                }
                            });
                        }
                    }
                } catch (error) {
                    console.warn(`Error calculating yield trendline for ${estate}:`, error);
                }
            });
        }

        return transformedData;
    }, [yieldBjrData, appliedFilters.estate, appliedFilters.estate_id]);

    // BJR Chart Data - Group by fiscal_year and calculate BJR (weight/bunch_qty * 1000) per estate
    const bjrChartData = useMemo(() => {
        if (!yieldBjrData || yieldBjrData.length === 0) return [];

        const source = (appliedFilters.estate && !appliedFilters.estate_id)
            ? yieldBjrData.filter(item => item.estate === appliedFilters.estate)
            : yieldBjrData;

        // Group by fiscal_year and estate
        const grouped: Record<string, Record<string, { total_weight: number; total_bunch_qty: number; count: number }>> = {};

        source.forEach(row => {
            const year = row.fiscal_year;
            if (!grouped[year]) grouped[year] = {};
            if (!grouped[year][row.estate]) {
                grouped[year][row.estate] = { total_weight: 0, total_bunch_qty: 0, count: 0 };
            }
            grouped[year][row.estate].total_weight += row.weight;
            grouped[year][row.estate].total_bunch_qty += row.bunch_qty;
            grouped[year][row.estate].count += 1;
        });

        // Get all unique estates and sort them in consistent order
        const allEstates = new Set<string>();
        source.forEach(item => allEstates.add(item.estate));

        // Define estate order for consistent legend ordering
        const estateOrder = ['LME', 'MME', 'PAM1', 'PAM2'];
        const sortedEstates = Array.from(allEstates).sort((a, b) => {
            const indexA = estateOrder.indexOf(a);
            const indexB = estateOrder.indexOf(b);
            if (indexA === -1 && indexB === -1) return a.localeCompare(b);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });

        // Sort years
        const sortedYears = Object.keys(grouped).sort();

        // Transform to chart format - calculate BJR as (weight/bunch_qty) * 1000
        const transformedData = sortedYears.map(year => {
            const result: Record<string, any> = { periode: year };
            sortedEstates.forEach(estate => {
                const estateData = grouped[year][estate];
                result[estate] = estateData && estateData.total_bunch_qty > 0
                    ? (estateData.total_weight / estateData.total_bunch_qty) * 1000
                    : 0;
            });
            return result;
        });

        // Calculate trendlines for each estate
        if (transformedData.length >= 2) {
            sortedEstates.forEach(estate => {
                try {
                    const points = transformedData
                        .map((d, i) => ({ x: i, y: d[estate] || 0 }))
                        .filter(p => typeof p.y === 'number' && !isNaN(p.y) && isFinite(p.y));

                    if (points.length >= 2) {
                        const { slope, intercept } = calculateTrendline(points);

                        // Only add trendline if values are valid
                        if (isFinite(slope) && isFinite(intercept)) {
                            transformedData.forEach((d, i) => {
                                const trendValue = getTrendValue(i, slope, intercept);
                                if (isFinite(trendValue)) {
                                    d[`${estate}_trend`] = trendValue;
                                }
                            });
                        }
                    }
                } catch (error) {
                    console.warn(`Error calculating BJR trendline for ${estate}:`, error);
                }
            });
        }

        return transformedData;
    }, [yieldBjrData, appliedFilters.estate, appliedFilters.estate_id]);

    // Harvest Attendance Chart Data
    const harvestAttendanceChartData = useMemo(() => {
        if (!harvestAttendanceData || harvestAttendanceData.length === 0) return [];

        // Filter by estate if selected and exclude 'LAINNYA'
        const source = appliedFilters.estate
            ? harvestAttendanceData.filter(item => item.estate === appliedFilters.estate && item.estate !== 'LAINNYA')
            : harvestAttendanceData.filter(item => item.estate !== 'LAINNYA');

        // Group by date range (start_date and end_date)
        const groupedByDate = source.reduce((result: Record<string, any[]>, item) => {
            const dateKey = `${item.start_date}|${item.end_date}`;
            if (!result[dateKey]) {
                result[dateKey] = [];
            }
            result[dateKey].push(item);
            return result;
        }, {});

        // Get all unique estates
        const allEstates = new Set<string>();
        source.forEach(item => {
            if (item.estate) allEstates.add(item.estate);
        });

        // Sort dates
        const dates = Object.keys(groupedByDate).sort((a, b) => {
            const dateA = groupedByDate[a][0].start_date;
            const dateB = groupedByDate[b][0].start_date;
            return new Date(dateA).getTime() - new Date(dateB).getTime();
        });

        // Function to format label_date from start_date and end_date
        const formatDateLabel = (startDate: string, endDate: string): string => {
            const start = new Date(startDate);
            const end = new Date(endDate);
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
            const startDay = start.getDate();
            const endDay = end.getDate();
            const monthStr = monthNames[end.getMonth()];
            return `${startDay}-${endDay} ${monthStr}`;
        };

        // Transform data
        const transformedData = dates.map(dateKey => {
            const [startDate, endDate] = dateKey.split('|');
            const dateItems = groupedByDate[dateKey];
            const label_date = formatDateLabel(startDate, endDate);
            const result: Record<string, any> = { label_date };

            // Initialize all estates with null/0
            Array.from(allEstates).sort().forEach(estate => {
                result[estate] = 0;
            });

            // Add actual values
            dateItems.forEach(item => {
                if (item.estate && item.total_pemanen > 0) {
                    // Calculate percentage: (total_fp / total_pemanen) * 100
                    const prc = (item.total_fp / item.total_pemanen) * 100;
                    result[item.estate] = prc;
                    result[`${item.estate}_fp_avail`] = item.total_pemanen;
                    result[`${item.estate}_fp_act`] = item.total_fp;
                }
            });

            return result;
        });

        // Calculate trendlines for each estate
        if (transformedData.length >= 2) {
            const estatesArray = Array.from(allEstates);
            estatesArray.forEach(estate => {
                try {
                    const points = transformedData
                        .map((d, i) => ({ x: i, y: d[estate] || 0 }))
                        .filter(p => typeof p.y === 'number' && !isNaN(p.y) && isFinite(p.y));

                    if (points.length >= 2) {
                        const { slope, intercept } = calculateTrendline(points);

                        if (isFinite(slope) && isFinite(intercept)) {
                            transformedData.forEach((d, i) => {
                                const trendValue = getTrendValue(i, slope, intercept);
                                if (isFinite(trendValue)) {
                                    d[`${estate}_trend`] = trendValue;
                                }
                            });
                        }
                    }
                } catch (error) {
                    console.warn(`Error calculating attendance trendline for ${estate}:`, error);
                }
            });
        }

        return transformedData;
    }, [harvestAttendanceData, appliedFilters.estate]);

    const harvestAttendanceDomain = useMemo(() => {
        if (!harvestAttendanceChartData || harvestAttendanceChartData.length === 0) {
            return [0, 100];
        }

        // Cari nilai prc terendah dari semua data
        let minPrc = 100;
        harvestAttendanceChartData.forEach(item => {
            Object.keys(item).forEach(key => {
                if (!key.endsWith('_trend') && !key.endsWith('_fp_avail') && !key.endsWith('_fp_act') && key !== 'label_date') {
                    const value = item[key];
                    if (typeof value === 'number' && value < minPrc) {
                        minPrc = value;
                    }
                }
            });
        });

        const lowerBound = calculateDynamicDomain(minPrc);
        return [lowerBound, 100];
    }, [harvestAttendanceChartData]);

    // Custom tooltip for Harvest Attendance
    const CustomTooltipAttendance = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <Box
                    bg="white"
                    p={3}
                    borderRadius="md"
                    boxShadow="lg"
                    borderWidth="1px"
                    maxWidth="300px"
                >
                    <Text fontWeight="bold" mb={1}>{label}</Text>
                    {payload
                        .filter((item: any) => !item.dataKey.endsWith('_trend')) // Filter out trend lines
                        .map((item: any, index: number) => {
                            const estateName = item.name;
                            const hkAvail = item.payload[`${estateName}_fp_avail`];
                            const hkAct = item.payload[`${estateName}_fp_act`];

                            return (
                                <Box key={index} mb={2}>
                                    <Text color={item.color} fontSize="sm" fontWeight="bold">
                                        {estateName}: {Math.round(item.value)}%
                                    </Text>
                                    {(hkAvail !== undefined || hkAct !== undefined) && (
                                        <Text fontSize="xs" color="gray.600" ml={2}>
                                            {hkAvail !== undefined && `Avail: ${formatNumber(hkAvail, 'number')} FP`}
                                            {hkAvail !== undefined && hkAct !== undefined && ' | '}
                                            {hkAct !== undefined && `Act: ${formatNumber2Digits(hkAct, 'number')} FP`}
                                        </Text>
                                    )}
                                </Box>
                            );
                        })
                    }
                </Box>
            );
        }
        return null;
    };

    // YTD Chart Data - Cumulative totals from Jan to selected month
    const ytdChartData = useMemo(() => {
        if (!dataFiltered || dataFiltered.length === 0) return null;

        const source = (appliedFilters.estate && !appliedFilters.estate_id)
            ? dataFiltered.filter(item => item.estate === appliedFilters.estate)
            : dataFiltered;

        const totalAktual = source.reduce((sum, item) => sum + item.weight_act, 0) / 1000;
        const totalTarget = source.reduce((sum, item) => sum + item.weight_bgt, 0) / 1000;
        const percentage = totalTarget > 0 ? (totalAktual / totalTarget) * 100 : 0;

        return {
            totalAktual,
            totalTarget,
            percentage
        };
    }, [dataFiltered, appliedFilters.estate, appliedFilters.estate_id]);

    // MTD Chart Data - Only selected month data
    const mtdChartData = useMemo(() => {
        if (!data || data.length === 0) return null;

        const tahun_bulan = `${appliedFilters.tahun}${appliedFilters.bulan}`;
        const source = (appliedFilters.estate && !appliedFilters.estate_id)
            ? data.filter(item => item.estate === appliedFilters.estate && item.periode === tahun_bulan)
            : data.filter(item => item.periode === tahun_bulan);

        const totalAktual = source.reduce((sum, item) => sum + item.weight_act, 0) / 1000;
        const totalTarget = source.reduce((sum, item) => sum + item.weight_bgt, 0) / 1000;
        const percentage = totalTarget > 0 ? (totalAktual / totalTarget) * 100 : 0;

        return {
            totalAktual,
            totalTarget,
            percentage
        };
    }, [data, appliedFilters.estate, appliedFilters.estate_id, appliedFilters.tahun, appliedFilters.bulan]);

    // Monthly Trend Chart Data - Line chart for all months
    const monthlyTrendData = useMemo(() => {
        if (!data || data.length === 0) return [];

        const tahun_bulan = `${appliedFilters.tahun}${appliedFilters.bulan}`;
        const source = (appliedFilters.estate && !appliedFilters.estate_id)
            ? data.filter(item => item.estate === appliedFilters.estate)
            : data;

        const grouped: Record<string, { periode: string; weight_act: number; weight_bgt: number }> = {};
        source.forEach(row => {
            const p = row.periode;
            if (!grouped[p]) grouped[p] = { periode: p, weight_act: 0, weight_bgt: 0 };
            grouped[p].weight_act += row.weight_act / 1000;
            grouped[p].weight_bgt += row.weight_bgt / 1000;
        });

        const sorted = Object.values(grouped)
            .sort((a, b) => a.periode.localeCompare(b.periode))
            .filter(d => d.periode <= tahun_bulan);

        interface MonthlyTrendData {
            periode: string;
            aktual: number;
            target: number;
            aktual_trend?: number;
            target_trend?: number;
        }

        const transformedData: MonthlyTrendData[] = sorted.map(d => ({
            periode: formatPeriode(d.periode),
            aktual: d.weight_act,
            target: d.weight_bgt
        }));

        // Calculate trendlines for actual and target
        if (transformedData.length >= 2) {
            // Trendline for aktual
            try {
                const actualPoints = transformedData
                    .map((d, i) => ({ x: i, y: d.aktual }))
                    .filter(p => typeof p.y === 'number' && !isNaN(p.y) && isFinite(p.y));

                if (actualPoints.length >= 2) {
                    const { slope: actualSlope, intercept: actualIntercept } = calculateTrendline(actualPoints);
                    if (isFinite(actualSlope) && isFinite(actualIntercept)) {
                        transformedData.forEach((d, i) => {
                            d.aktual_trend = getTrendValue(i, actualSlope, actualIntercept);
                        });
                    }
                }
            } catch (error) {
                console.warn('Error calculating actual trendline:', error);
            }

            // Trendline for target
            try {
                const targetPoints = transformedData
                    .map((d, i) => ({ x: i, y: d.target }))
                    .filter(p => typeof p.y === 'number' && !isNaN(p.y) && isFinite(p.y));

                if (targetPoints.length >= 2) {
                    const { slope: targetSlope, intercept: targetIntercept } = calculateTrendline(targetPoints);
                    if (isFinite(targetSlope) && isFinite(targetIntercept)) {
                        transformedData.forEach((d, i) => {
                            d.target_trend = getTrendValue(i, targetSlope, targetIntercept);
                        });
                    }
                }
            } catch (error) {
                console.warn('Error calculating target trendline:', error);
            }
        }

        return transformedData;
    }, [data, appliedFilters.estate, appliedFilters.estate_id, appliedFilters.tahun, appliedFilters.bulan]);


    const productionTrendData = useMemo(() => {
        if (!dataFiltered || dataFiltered.length === 0) return [];

        // kalau ada estate terpilih, pakai hanya data dari estate itu
        const source = (appliedFilters.estate && !appliedFilters.estate_id)
            ? dataFiltered.filter(item => item.estate === appliedFilters.estate)
            : dataFiltered;
        const grouped = source.reduce(
            (acc: Record<string, { estate: string; aktual: number; budget: number }>, cur) => {
                const key = (appliedFilters.estate || appliedFilters.estate_id) ? cur.division : cur.estate;
                if (!acc[key]) acc[key] = { estate: key, aktual: 0, budget: 0 };
                acc[key].aktual += cur.weight_act / 1000;
                acc[key].budget += cur.weight_bgt / 1000;
                return acc;
            },
            {}
        );
        return Object.values(grouped);
    }, [dataFiltered, appliedFilters.estate, appliedFilters.estate_id]);

    const CustomTooltipTrend = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <Box bg="white" p={3} borderRadius="md" boxShadow="lg" borderWidth="1px">
                    <Text fontWeight="bold" mb={1}>{label}</Text>
                    {payload.map((item: any, idx: number) => {
                        const isPct = item.dataKey === 'cumAchievementPrc';
                        const val = isPct
                            ? `${item.value.toFixed(1)}%`
                            : formatNumber(item.value, 'number') + ' ton';
                        return (
                            <Text key={idx} color={item.color} fontSize="sm">
                                {item.name}: {val}
                            </Text>
                        );
                    })}
                </Box>
            )
        }
        return null;
    };

    const CustomTooltipWeekly = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <Box bg="white" p={3} borderRadius="md" boxShadow="lg" borderWidth="1px" maxW="300px">
                    <Text fontWeight="bold" mb={1}>{label}</Text>
                    {payload
                        .filter((item: any) => item.value > 0 && !item.dataKey.endsWith('_trend')) // Filter out trend lines and zero values
                        .map((item: any, idx: number) => (
                            <Text key={idx} color={item.color} fontSize="sm">
                                {item.name}: {formatNumber(item.value, 'number')} kg
                            </Text>
                        ))
                    }
                </Box>
            );
        }
        return null;
    };

    const totalLuasInti = useMemo(
        () => {
            if (!data || data.length === 0) return 0;

            return data.filter(item => item.block_status === 'Kebun Inti')
                .reduce((sum, item) => sum + item.planted_area, 0);
        },
        [data]
    )

    const totalLuasPlasma = useMemo(
        () => {
            if (!data || data.length === 0) return 0;

            return data.filter(item => item.block_status === 'Kebun Plasma')
                .reduce((sum, item) => sum + item.planted_area, 0);
        },
        [data]
    )

    const headerPadding = useBreakpointValue({
        base: 2,
        md: 2,
        lg: 2,
        xl: 2,
        '2xl': 3
    });
    const headerMargin = useBreakpointValue({
        base: 2,
        md: 2,
        lg: 2,
        xl: 2
    });

    // Responsive values
    const isMobile = useBreakpointValue({ base: true, md: false });
    const headingSize = useBreakpointValue({ base: 'sm', md: 'md', lg: 'md' });
    const inputSize = useBreakpointValue({ base: 'xs', md: 'sm' });
    const buttonSize = useBreakpointValue({ base: 'xs', md: 'sm' });
    const labelFontSize = useBreakpointValue({ base: 'sm', md: 'sm' });
    const selectWidth = useBreakpointValue({ base: '180px', md: '200px', lg: '220px' });
    const buttonPadding = useBreakpointValue({ base: 2, md: 3 });

    // Get year options (3 years back from current year)
    const getYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = 0; i < 3; i++) {
            years.push(currentYear - i);
        }
        return years.sort((a, b) => b - a);
    };

    // Month options
    const monthOptions = Array.from({ length: 12 }, (_, i) => ({
        value: (i + 1).toString().padStart(2, '0'),
        label: new Date(2000, i).toLocaleString('id-ID', { month: 'long' })
    }));

    const bgColor = useColorModeValue('white', 'gray.800')

    return (
        <Box minH="100vh" bg="gray.100" p={2} w="100%">
            {/* Loading Overlay */}
            {loading && (
                <Center
                    position="fixed"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bg="rgba(0,0,0,0.1)"
                    zIndex={9999}
                    flexDirection="column"
                >
                    <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="gray.200"
                        color="blue.500"
                        size="xl"
                        mb={4}
                    />
                    <Text fontSize="lg" color="gray.700">
                        Memuat data produksi...
                    </Text>
                </Center>
            )}

            {/* Filter Section */}
            <Box bg="white" rounded="lg" shadow="md" p={headerPadding} mb={headerMargin} w="100%">
                {/* Desktop Layout */}
                {!isMobile ? (
                    <Flex justify="space-between" align="center" w="100%" flexWrap="wrap" gap={2}>
                        <Heading
                            as="h1"
                            size={headingSize}
                            color="gray.800"
                            minW="fit-content"
                        >
                            DASHBOARD KEBUN
                        </Heading>

                        {/* Year Filter */}
                        <Flex wrap="wrap" gap={2} align="center" minW="fit-content">
                            <FormControl w="auto" size={inputSize}>
                                <Flex align="center" gap={1} flexWrap="wrap">
                                    <FormLabel
                                        htmlFor="year"
                                        fontSize={labelFontSize}
                                        color="gray.700"
                                        mb={0}
                                        whiteSpace="nowrap"
                                    >
                                        Tahun:
                                    </FormLabel>
                                    <Select
                                        id="year"
                                        value={filters.tahun}
                                        onChange={(e) => setFilters(prev => ({ ...prev, tahun: e.target.value }))}
                                        size={inputSize}
                                        borderRadius="md"
                                        focusBorderColor="blue.500"
                                        bg="white"
                                        w={selectWidth}
                                    >
                                        {getYearOptions().map(year => (
                                            <option key={year} value={year.toString()}>
                                                {year}
                                            </option>
                                        ))}
                                    </Select>
                                </Flex>
                            </FormControl>

                            {/* Month Filter */}
                            <FormControl w="auto" size={inputSize}>
                                <Flex align="center" gap={1} flexWrap="wrap">
                                    <FormLabel
                                        htmlFor="month"
                                        fontSize={labelFontSize}
                                        color="gray.700"
                                        mb={0}
                                        whiteSpace="nowrap"
                                    >
                                        Bulan:
                                    </FormLabel>
                                    <Select
                                        id="month"
                                        value={filters.bulan}
                                        onChange={(e) => setFilters(prev => ({ ...prev, bulan: e.target.value }))}
                                        size={inputSize}
                                        borderRadius="md"
                                        focusBorderColor="blue.500"
                                        bg="white"
                                        w={selectWidth}
                                    >
                                        {monthOptions.map(month => (
                                            <option key={month.value} value={month.value}>
                                                {month.label}
                                            </option>
                                        ))}
                                    </Select>
                                </Flex>
                            </FormControl>

                            {/* Company Filter */}
                            <FormControl w="auto" size={inputSize}>
                                <Flex align="center" gap={1} flexWrap="wrap">
                                    <FormLabel
                                        htmlFor="company"
                                        fontSize={labelFontSize}
                                        color="gray.700"
                                        mb={0}
                                        whiteSpace="nowrap"
                                    >
                                        Company:
                                    </FormLabel>
                                    <Select
                                        id="company"
                                        value={filters.company_id || ""}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setFilters(prev => ({
                                                ...prev,
                                                company_id: val ? Number(val) : 1,
                                                estate_id: undefined,
                                                estate: undefined
                                            }));
                                        }}
                                        size={inputSize}
                                        borderRadius="md"
                                        focusBorderColor="blue.500"
                                        bg="white"
                                        w={selectWidth}
                                    >
                                        {companies.map(company => (
                                            <option key={company.id} value={company.id}>
                                                {company.name}
                                            </option>
                                        ))}
                                    </Select>
                                </Flex>
                            </FormControl>

                            {/* Estate Filter */}
                            <FormControl w="auto" size={inputSize}>
                                <Flex align="center" gap={1} flexWrap="wrap">
                                    <FormLabel
                                        htmlFor="estate"
                                        fontSize={labelFontSize}
                                        color="gray.700"
                                        mb={0}
                                        whiteSpace="nowrap"
                                    >
                                        Estate:
                                    </FormLabel>
                                    <Select
                                        id="estate"
                                        value={filters.estate_id || ""}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val) {
                                                const selectedEstate = estates.find(est => est.odoo_id.toString() === val);
                                                setFilters(prev => ({
                                                    ...prev,
                                                    estate_id: Number(val),
                                                    estate: selectedEstate ? selectedEstate.name : undefined
                                                }));
                                            } else {
                                                setFilters(prev => ({
                                                    ...prev,
                                                    estate_id: undefined,
                                                    estate: undefined
                                                }));
                                            }
                                        }}
                                        placeholder="Semua Estate"
                                        size={inputSize}
                                        borderRadius="md"
                                        focusBorderColor="blue.500"
                                        bg="white"
                                        w={selectWidth}
                                    >
                                        {filteredEstates.map(estate => (
                                            <option key={estate.id} value={estate.odoo_id}>
                                                {estate.name}
                                            </option>
                                        ))}
                                    </Select>
                                </Flex>
                            </FormControl>

                            <Button
                                onClick={handleRefresh}
                                isLoading={loading}
                                loadingText="Memuat..."
                                colorScheme="blue"
                                size={buttonSize}
                                px={buttonPadding}
                                fontWeight="medium"
                            // leftIcon={<FaFilter />}
                            >
                                Refresh
                            </Button>
                        </Flex>
                    </Flex>
                ) : (
                    /* Mobile Layout */
                    <Flex direction="column" gap={2} w="100%">
                        <Heading
                            as="h1"
                            size="sm"
                            color="gray.800"
                            textAlign="center"
                        >
                            DASHBOARD KEBUN
                        </Heading>

                        {/* Year Filter - Mobile/Tablet */}
                        <Box flex={1}>
                            <FormLabel
                                htmlFor="year"
                                fontSize={labelFontSize}
                                color="gray.700"
                                mb={0}
                                whiteSpace="nowrap"
                            >
                                Tahun
                            </FormLabel>
                            <Select
                                id="year"
                                value={filters.tahun}
                                onChange={(e) => setFilters(prev => ({ ...prev, tahun: e.target.value }))}
                                size="sm"
                                borderRadius="md"
                                focusBorderColor="blue.500"
                            >
                                {getYearOptions().map(year => (
                                    <option key={year} value={year.toString()}>
                                        {year}
                                    </option>
                                ))}
                            </Select>
                        </Box>

                        {/* Month Filter - Mobile/Tablet */}
                        <Box flex={1}>
                            <FormLabel
                                htmlFor="month"
                                fontSize={labelFontSize}
                                color="gray.700"
                                mb={0}
                                whiteSpace="nowrap"
                            >
                                Bulan
                            </FormLabel>
                            <Select
                                id="month"
                                value={filters.bulan}
                                onChange={(e) => setFilters(prev => ({ ...prev, bulan: e.target.value }))}
                                size="sm"
                                borderRadius="md"
                                focusBorderColor="blue.500"
                            >
                                {monthOptions.map(month => (
                                    <option key={month.value} value={month.value}>
                                        {month.label}
                                    </option>
                                ))}
                            </Select>
                        </Box>

                        {/* Company Filter - Mobile/Tablet */}
                        <Box flex={1}>
                            <FormLabel
                                htmlFor="company_mobile"
                                fontSize={labelFontSize}
                                color="gray.700"
                                mb={0}
                                whiteSpace="nowrap"
                            >
                                Company
                            </FormLabel>
                            <Select
                                id="company_mobile"
                                value={filters.company_id || ""}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFilters(prev => ({
                                        ...prev,
                                        company_id: val ? Number(val) : 1,
                                        estate_id: undefined,
                                        estate: undefined
                                    }));
                                }}
                                size="sm"
                                borderRadius="md"
                                focusBorderColor="blue.500"
                            >
                                {companies.map(company => (
                                    <option key={company.id} value={company.id}>
                                        {company.name}
                                    </option>
                                ))}
                            </Select>
                        </Box>

                        <Box flex={1}>
                            <FormLabel
                                htmlFor="estate_mobile"
                                fontSize={labelFontSize}
                                color="gray.700"
                                mb={0}
                                whiteSpace="nowrap"
                            >
                                Estate
                            </FormLabel>
                            <Select
                                id="estate_mobile"
                                value={filters.estate_id || ""}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val) {
                                        const selectedEstate = estates.find(est => est.odoo_id.toString() === val);
                                        setFilters(prev => ({
                                            ...prev,
                                            estate_id: Number(val),
                                            estate: selectedEstate ? selectedEstate.name : undefined
                                        }));
                                    } else {
                                        setFilters(prev => ({
                                            ...prev,
                                            estate_id: undefined,
                                            estate: undefined
                                        }));
                                    }
                                }}
                                placeholder="Semua Estate"
                                size="sm"
                                borderRadius="md"
                                focusBorderColor="blue.500"
                            >
                                {filteredEstates.map(estate => (
                                    <option key={estate.id} value={estate.odoo_id}>
                                        {estate.name}
                                    </option>
                                ))}
                            </Select>
                        </Box>

                        <Button
                            colorScheme="blue"
                            onClick={handleRefresh}
                            alignSelf="flex-end"
                            // leftIcon={<FaFilter />}
                            size="sm"
                        >
                            Refresh
                        </Button>
                    </Flex>
                )}
            </Box>

            {loading ? (
                <Box>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
                        {[1, 2, 3].map((item) => (
                            <Skeleton key={item} height="100px" borderRadius="md" />
                        ))}
                    </SimpleGrid>
                    <Skeleton height="500px" borderRadius="md" mb={6} />
                    <Skeleton height="500px" borderRadius="md" mb={6} />
                </Box>
            ) : (
                <Box mb={6}>
                    {/* YTD, MTD, and Estate Production */}
                    <Flex
                        direction={{ base: 'column', md: 'row' }}
                        gap={4}
                        mb={4}
                    >
                        {/* Chart 1: Produksi TBS YTD - 25% width */}
                        <Box
                            bg={bgColor}
                            rounded="lg"
                            shadow="md"
                            overflow="hidden"
                            display="flex"
                            flexDirection="column"
                            flex={{ base: '1', md: '0 0 calc(25% - 12px)' }}
                            height="45vh"
                        >
                            <Box bg="white" p={4} display="flex" flexDirection="column" boxShadow="md" height="100%" rounded="lg">
                                <Text fontSize="lg" fontWeight="semibold" mb={1}>
                                    Produksi TBS YTD
                                </Text>
                                <Text fontSize="md" textAlign="center" fontWeight="bold" color={ytdChartData && ytdChartData.percentage >= 100 ? "green.600" : "orange.600"} mb={1}>
                                    {ytdChartData ? `${ytdChartData.percentage.toFixed(1)}%` : '0%'}
                                </Text>
                                <Box flex={1} minH="250px">
                                    {ytdChartData ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={[
                                                    { name: 'Aktual', value: ytdChartData.totalAktual, fill: _ACTUAL },
                                                    { name: 'Target', value: ytdChartData.totalTarget, fill: _TARGET }
                                                ]}
                                                margin={{ top: 15, right: 10, left: 10, bottom: 0 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                                <YAxis
                                                    tick={{ fontSize: 12 }}
                                                    tickFormatter={(value) => formatNumber(value, 'number')}
                                                    width={60}
                                                    label={{
                                                        value: 'Ton',
                                                        angle: -90,
                                                        position: 'insideLeft',
                                                        offset: -5,
                                                        fontSize: 12,
                                                        fill: '#666',
                                                    }}
                                                />
                                                <Tooltip
                                                    content={<CustomTooltipBar />}
                                                    wrapperStyle={{ zIndex: 1000 }}
                                                />
                                                <Bar
                                                    dataKey="value"
                                                    label={<CustomBarLabel />}
                                                    barSize={100}
                                                >
                                                    {[
                                                        { name: 'Aktual', value: ytdChartData.totalAktual, fill: _ACTUAL },
                                                        { name: 'Target', value: ytdChartData.totalTarget, fill: _TARGET }
                                                    ].map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.fill} />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <Center h="100%">
                                            <Text color="gray.500">Tidak ada data</Text>
                                        </Center>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        {/* Chart 2: Produksi TBS MTD - 25% width */}
                        <Box
                            bg={bgColor}
                            rounded="lg"
                            shadow="md"
                            overflow="hidden"
                            display="flex"
                            flexDirection="column"
                            flex={{ base: '1', md: '0 0 calc(25% - 12px)' }}
                            height="45vh"
                        >
                            <Box bg="white" p={4} display="flex" flexDirection="column" boxShadow="md" height="100%" rounded="lg">
                                <Text fontSize="lg" fontWeight="semibold" mb={1}>
                                    Produksi TBS MTD
                                </Text>
                                <Text fontSize="md" textAlign="center" fontWeight="bold" color={mtdChartData && mtdChartData.percentage >= 100 ? "green.600" : "orange.600"} mb={3}>
                                    {mtdChartData ? `${mtdChartData.percentage.toFixed(1)}%` : '0%'}
                                </Text>
                                <Box flex={1} minH="250px">
                                    {mtdChartData ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={[
                                                    { name: 'Aktual', value: mtdChartData.totalAktual, fill: _ACTUAL },
                                                    { name: 'Target', value: mtdChartData.totalTarget, fill: _TARGET }
                                                ]}
                                                margin={{ top: 15, right: 10, left: 10, bottom: 0 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                                <YAxis
                                                    tick={{ fontSize: 12 }}
                                                    tickFormatter={(value) => formatNumber(value, 'number')}
                                                    width={60}
                                                    label={{
                                                        value: 'Ton',
                                                        angle: -90,
                                                        position: 'insideLeft',
                                                        offset: 0,
                                                        fontSize: 12,
                                                        fill: '#666',
                                                    }}
                                                />
                                                <Tooltip
                                                    content={<CustomTooltipBar />}
                                                    wrapperStyle={{ zIndex: 1000 }}
                                                />
                                                <Bar
                                                    dataKey="value"
                                                    label={<CustomBarLabel />}
                                                    barSize={100}
                                                >
                                                    {[
                                                        { name: 'Aktual', value: mtdChartData.totalAktual, fill: _ACTUAL },
                                                        { name: 'Target', value: mtdChartData.totalTarget, fill: _TARGET }
                                                    ].map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.fill} />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <Center h="100%">
                                            <Text color="gray.500">Tidak ada data</Text>
                                        </Center>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        {/* Produksi TBS per Estate - 50% width (Moved from Row 2) */}
                        <Box
                            bg={bgColor}
                            rounded="lg"
                            shadow="md"
                            overflow="hidden"
                            display="flex"
                            flexDirection="column"
                            flex={{ base: '1', md: '0 0 calc(50% - 12px)' }}
                            height="45vh"
                        >
                            <Box bg="white" p={4} display="flex" flexDirection="column" boxShadow="md" height="100%" rounded="lg">
                                <Text fontSize="xl" fontWeight="semibold" mb={4} color="gray.700">
                                    {appliedFilters.estate
                                        ? `Produksi TBS ${appliedFilters.estate}`
                                        : 'Produksi TBS per Estate'}
                                </Text>
                                <Box
                                    minH="130px"
                                    height="350px"
                                    w="100%"
                                >
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={productionTrendData}
                                            barGap={0}
                                            margin={{ top: 5, right: 10, left: 10, bottom: -5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                            <XAxis
                                                dataKey="estate"
                                                interval={isMobile ? undefined : 0}
                                                tick={<CustomizedAxisTick />}
                                            />
                                            <YAxis
                                                tickFormatter={(value) => formatNumber(value, 'number')}
                                                width={70}
                                                tick={{ fontSize: 12 }}
                                                label={{
                                                    value: 'Ton',
                                                    angle: -90,
                                                    position: 'insideLeft',
                                                    offset: 5,
                                                    fontSize: 14,
                                                    fill: '#666'
                                                }}
                                            />
                                            <Tooltip
                                                content={<CustomTooltipTon />}
                                                wrapperStyle={{ zIndex: 1000 }}
                                            />
                                            <Legend
                                                verticalAlign="top"
                                                height={36}
                                            />
                                            <Bar
                                                name="Aktual"
                                                dataKey="aktual"
                                                fill={_ACTUAL}
                                                minPointSize={8}
                                            />
                                            <Bar
                                                name="Budget"
                                                dataKey="budget"
                                                fill={_TARGET}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            </Box>
                        </Box>
                    </Flex>

                    {/* Row 2: Produksi TBS Bulanan - Full Width */}
                    <Box
                        bg={bgColor}
                        rounded="lg"
                        shadow="md"
                        overflow="hidden"
                        display="flex"
                        flexDirection="column"
                        height="55vh" // Increased height as requested
                        mb={4}
                    >
                        <Box bg="white" p={4} display="flex" flexDirection="column" boxShadow="md" height="100%" rounded="lg">
                            <Text fontSize="lg" fontWeight="semibold" mb={4}>
                                Produksi TBS Bulanan
                            </Text>
                            <Box flex={1} minH="250px">
                                {monthlyTrendData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart
                                            data={monthlyTrendData}
                                            margin={{ top: 5, right: 10, left: 10, bottom: 0 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="periode"
                                                tick={{ fontSize: 11 }}
                                                angle={-45}
                                                textAnchor="end"
                                            // height={60}
                                            />
                                            <YAxis
                                                tick={{ fontSize: 12 }}
                                                tickFormatter={(value) => formatNumber(value, 'number')}
                                                width={60}
                                                label={{
                                                    value: 'Ton',
                                                    angle: -90,
                                                    position: 'insideLeft',
                                                    offset: 5,
                                                    fontSize: 12,
                                                    fill: '#666',
                                                }}
                                            />
                                            <Tooltip
                                                content={<CustomTooltipMonthly />}
                                                wrapperStyle={{ zIndex: 1000 }}
                                            />
                                            <Legend
                                                verticalAlign="top"
                                                height={36}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="aktual"
                                                name="Aktual"
                                                stroke={_ACTUAL}
                                                strokeWidth={1}
                                                dot={{ r: 4 }}
                                                activeDot={{ r: 6 }}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="aktual_trend"
                                                name="Tren Aktual"
                                                stroke={_ACTUAL}
                                                strokeWidth={1}
                                                strokeDasharray="5 5"
                                                dot={false}
                                                legendType="none"
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="target"
                                                name="Target"
                                                stroke={_TARGET}
                                                strokeWidth={1}
                                                dot={{ r: 4 }}
                                                activeDot={{ r: 6 }}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="target_trend"
                                                name="Tren Target"
                                                stroke={_TARGET}
                                                strokeWidth={1}
                                                strokeDasharray="5 5"
                                                dot={false}
                                                legendType="none"
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Center h="100%">
                                        <Text color="gray.500">Tidak ada data</Text>
                                    </Center>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* Row 3: Weekly Production and Harvest Attendance */}
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} padding={0}>
                        {/* Weekly Production Chart */}
                        <Box
                            bg={bgColor}
                            rounded="lg"
                            shadow="md"
                            overflow="hidden"
                            display="flex"
                            flexDirection="column"
                            height="45vh"
                        >
                            <Box bg="white" p={4} display="flex" flexDirection="column" boxShadow="md" height="100%" rounded="lg">
                                <Text fontSize="xl" fontWeight="semibold" mb={4} color="gray.700">
                                    Produksi TBS per Minggu
                                </Text>
                                {weeklyLoading ? (
                                    <Center flex={1}>
                                        <Spinner
                                            thickness="4px"
                                            speed="0.65s"
                                            emptyColor="gray.200"
                                            color="blue.500"
                                            size="xl"
                                        />
                                    </Center>
                                ) : weeklyChartData.length > 0 ? (
                                    <Box overflow="auto" width="100%" flex={1}>
                                        <Box minWidth="600px" height="100%">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart
                                                    data={weeklyChartData}
                                                    margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
                                                >
                                                    {/* <CartesianGrid strokeDasharray="3 3" /> */}
                                                    <XAxis
                                                        dataKey="label_date"
                                                        height={30}
                                                        tick={{ fontSize: 11 }}
                                                        interval={0}
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 12 }}
                                                        label={{
                                                            value: 'Kg',
                                                            angle: -90,
                                                            offset: -10,
                                                            fontSize: 14,
                                                            position: 'insideLeft'
                                                        }}
                                                        tickFormatter={(value) => formatNumber(value, 'number')}
                                                        width={70}
                                                    />
                                                    <Tooltip content={<CustomTooltipWeekly />} />
                                                    <Legend
                                                        onMouseEnter={handleMouseEnter}
                                                        onMouseLeave={handleMouseLeave}
                                                    />
                                                    {weeklyChartData.length > 0 &&
                                                        Object.keys(weeklyChartData[0])
                                                            .filter(key => key !== 'label_date' && !key.endsWith('_trend'))
                                                            .map((supplier, index) => {
                                                                const opacity = hoveringDataKey === undefined || hoveringDataKey === supplier ? 1 : 0.3;
                                                                return (
                                                                    <Line
                                                                        type="monotone"
                                                                        key={supplier}
                                                                        dataKey={supplier}
                                                                        name={supplier}
                                                                        stroke={COLORS2[index % COLORS2.length]}
                                                                        activeDot={{ r: 8 }}
                                                                        strokeWidth={1}
                                                                        strokeOpacity={opacity}
                                                                    />
                                                                )
                                                            })
                                                    }
                                                    {weeklyChartData.length > 1 &&
                                                        Object.keys(weeklyChartData[0])
                                                            .filter(key => key !== 'label_date' && !key.endsWith('_trend'))
                                                            .map((supplier, index) => {
                                                                const opacity = hoveringDataKey === undefined || hoveringDataKey === supplier ? 0.6 : 0.2;
                                                                return (
                                                                    <Line
                                                                        key={`${supplier}_trend`}
                                                                        dataKey={`${supplier}_trend`}
                                                                        stroke={COLORS2[index % COLORS2.length]}
                                                                        strokeDasharray="5 5"
                                                                        strokeWidth={1}
                                                                        dot={false}
                                                                        activeDot={false}
                                                                        legendType="none"
                                                                        strokeOpacity={opacity}
                                                                    />
                                                                )
                                                            })
                                                    }
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Center flex={1}>
                                        <Text color="gray.500">Tidak ada data produksi mingguan</Text>
                                    </Center>
                                )}
                            </Box>
                        </Box>

                        {/* Harvest Attendance Chart */}
                        <Box
                            bg={bgColor}
                            rounded="lg"
                            shadow="md"
                            overflow="hidden"
                            display="flex"
                            flexDirection="column"
                            height="45vh"
                        >
                            <Box bg="white" p={4} display="flex" flexDirection="column" boxShadow="md" height="100%" rounded="lg">
                                <Text fontSize="xl" fontWeight="semibold" mb={4} color="gray.700">
                                    Kehadiran Pemanen per Minggu
                                </Text>
                                {harvestAttendanceLoading ? (
                                    <Center flex={1}>
                                        <Spinner
                                            thickness="4px"
                                            speed="0.65s"
                                            emptyColor="gray.200"
                                            color="blue.500"
                                            size="xl"
                                        />
                                    </Center>
                                ) : harvestAttendanceChartData.length > 0 ? (
                                    <Box overflow="auto" width="100%" flex={1}>
                                        <Box minWidth="600px" height="100%">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart
                                                    data={harvestAttendanceChartData}
                                                    margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
                                                >
                                                    <XAxis
                                                        dataKey="label_date"
                                                        height={30}
                                                        tick={{ fontSize: 11 }}
                                                        interval={0}
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 12 }}
                                                        label={{
                                                            value: '%',
                                                            angle: -90,
                                                            offset: -10,
                                                            fontSize: 14,
                                                            position: 'insideLeft'
                                                        }}
                                                        tickFormatter={(value) => `${Math.round(value)}`}
                                                        width={40}
                                                        domain={harvestAttendanceDomain}
                                                        allowDataOverflow={true}
                                                    />
                                                    <Tooltip content={<CustomTooltipAttendance />} />
                                                    <Legend
                                                        onMouseEnter={handleMouseEnter}
                                                        onMouseLeave={handleMouseLeave}
                                                    />
                                                    {harvestAttendanceChartData.length > 0 &&
                                                        Object.keys(harvestAttendanceChartData[0])
                                                            .filter(key => key !== 'label_date' && !key.endsWith('_trend') && !key.endsWith('_fp_avail') && !key.endsWith('_fp_act'))
                                                            .map((estate, index) => {
                                                                const opacity = hoveringDataKey === undefined || hoveringDataKey === estate ? 1 : 0.3;
                                                                return (
                                                                    <Line
                                                                        type="monotone"
                                                                        key={estate}
                                                                        dataKey={estate}
                                                                        name={estate}
                                                                        stroke={COLORS[index % COLORS.length]}
                                                                        activeDot={{ r: 8 }}
                                                                        strokeWidth={1}
                                                                        strokeOpacity={opacity}
                                                                    />

                                                                )
                                                            })
                                                    }
                                                    {harvestAttendanceChartData.length > 1 &&
                                                        Object.keys(harvestAttendanceChartData[0])
                                                            .filter(key => key !== 'label_date' && !key.endsWith('_trend') && !key.endsWith('_fp_avail') && !key.endsWith('_fp_act'))
                                                            .map((estate, index) => {
                                                                const opacity = hoveringDataKey === undefined || hoveringDataKey === estate ? 0.6 : 0.2;
                                                                return (
                                                                    <Line
                                                                        key={`${estate}_trend`}
                                                                        dataKey={`${estate}_trend`}
                                                                        stroke={COLORS[index % COLORS.length]}
                                                                        strokeDasharray="5 5"
                                                                        strokeWidth={1}
                                                                        strokeOpacity={opacity}
                                                                        dot={false}
                                                                        activeDot={false}
                                                                        legendType="none"
                                                                    />
                                                                )
                                                            })
                                                    }
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Center flex={1}>
                                        <Text color="gray.500">Tidak ada data kehadiran pemanen</Text>
                                    </Center>
                                )}
                            </Box>
                        </Box>
                    </SimpleGrid>

                    {/* Tren Yield and Tren BJR Charts */}
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} padding={0} mt={4}>
                        {/* Tren Yield Chart */}
                        <Box
                            bg={bgColor}
                            rounded="lg"
                            shadow="md"
                            overflow="hidden"
                            display="flex"
                            flexDirection="column"
                            height="45vh"
                        >
                            <Box bg="white" p={4} display="flex" flexDirection="column" boxShadow="md" height="100%" rounded="lg">
                                <Text fontSize="xl" fontWeight="semibold" mb={4} color="gray.700">
                                    Tren Yield
                                </Text>
                                {yieldBjrLoading ? (
                                    <Center flex={1}>
                                        <Spinner
                                            thickness="4px"
                                            speed="0.65s"
                                            emptyColor="gray.200"
                                            color="blue.500"
                                            size="xl"
                                        />
                                    </Center>
                                ) : yieldChartData.length > 0 ? (
                                    <Box overflow="auto" width="100%" flex={1}>
                                        <Box minWidth="600px" height="100%">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart
                                                    data={yieldChartData}
                                                    margin={{ top: 10, right: 30, left: 20, bottom: 0 }}
                                                >
                                                    {/* <CartesianGrid strokeDasharray="3 3" /> */}
                                                    <XAxis
                                                        dataKey="periode"
                                                        height={30}
                                                        tick={{ fontSize: 11 }}
                                                        interval={0}
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 12 }}
                                                        label={{
                                                            value: 'Ton',
                                                            angle: -90,
                                                            offset: -10,
                                                            fontSize: 14,
                                                            position: 'insideLeft'
                                                        }}
                                                        tickFormatter={(value) => formatNumber2Digits(value, 'number')}
                                                        width={80}
                                                    />
                                                    <Tooltip content={<CustomTooltipYield />} />
                                                    <Legend
                                                        onMouseEnter={handleMouseEnter}
                                                        onMouseLeave={handleMouseLeave}
                                                    />
                                                    {yieldChartData.length > 0 &&
                                                        Object.keys(yieldChartData[0])
                                                            .filter(key => key !== 'periode' && !key.endsWith('_trend'))
                                                            .map((estate, index) => {
                                                                const opacity = hoveringDataKey === undefined || hoveringDataKey === estate ? 1 : 0.3;
                                                                return (
                                                                    <Line
                                                                        key={estate}
                                                                        type="monotone"
                                                                        dataKey={estate}
                                                                        name={estate}
                                                                        stroke={COLORS[index % COLORS.length]}
                                                                        activeDot={{ r: 8 }}
                                                                        strokeWidth={1}
                                                                        strokeOpacity={opacity}
                                                                    />
                                                                );
                                                            })
                                                    }
                                                    {yieldChartData.length > 1 &&
                                                        Object.keys(yieldChartData[0])
                                                            .filter(key => key !== 'periode' && !key.endsWith('_trend'))
                                                            .map((estate, index) => {
                                                                const opacity = hoveringDataKey === undefined || hoveringDataKey === estate ? 0.6 : 0.2;
                                                                return (
                                                                    <Line
                                                                        key={`${estate}_trend`}
                                                                        type="monotone"
                                                                        dataKey={`${estate}_trend`}
                                                                        stroke={COLORS[index % COLORS.length]}
                                                                        strokeDasharray="5 5"
                                                                        strokeWidth={1}
                                                                        strokeOpacity={opacity}
                                                                        dot={false}
                                                                        activeDot={false}
                                                                        legendType="none"
                                                                    />
                                                                );
                                                            })
                                                    }
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Center flex={1}>
                                        <Text color="gray.500">Tidak ada data yield</Text>
                                    </Center>
                                )}
                            </Box>
                        </Box>

                        {/* Tren BJR Chart */}
                        <Box
                            bg={bgColor}
                            rounded="lg"
                            shadow="md"
                            overflow="hidden"
                            display="flex"
                            flexDirection="column"
                            height="45vh"
                        >
                            <Box bg="white" p={4} display="flex" flexDirection="column" boxShadow="md" height="100%" rounded="lg">
                                <Text fontSize="xl" fontWeight="semibold" mb={4} color="gray.700">
                                    Tren BJR
                                </Text>
                                {yieldBjrLoading ? (
                                    <Center flex={1}>
                                        <Spinner
                                            thickness="4px"
                                            speed="0.65s"
                                            emptyColor="gray.200"
                                            color="blue.500"
                                            size="xl"
                                        />
                                    </Center>
                                ) : bjrChartData.length > 0 ? (
                                    <Box overflow="auto" width="100%" flex={1}>
                                        <Box minWidth="600px" height="100%">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart
                                                    data={bjrChartData}
                                                    margin={{ top: 10, right: 30, left: 20, bottom: 0 }}
                                                >
                                                    <XAxis
                                                        dataKey="periode"
                                                        height={30}
                                                        tick={{ fontSize: 11 }}
                                                        interval={0}
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 12 }}
                                                        label={{
                                                            value: 'kg',
                                                            angle: -90,
                                                            offset: -10,
                                                            fontSize: 14,
                                                            position: 'insideLeft'
                                                        }}
                                                        tickFormatter={(value) => formatNumber2Digits(value, 'number')}
                                                        width={80}
                                                    />
                                                    <Tooltip content={<CustomTooltipBjr />} />
                                                    <Legend
                                                        onMouseEnter={handleMouseEnter}
                                                        onMouseLeave={handleMouseLeave}
                                                    />
                                                    {bjrChartData.length > 0 &&
                                                        Object.keys(bjrChartData[0])
                                                            .filter(key => key !== 'periode' && !key.endsWith('_trend'))
                                                            .map((estate, index) => {
                                                                const opacity = hoveringDataKey === undefined || hoveringDataKey === estate ? 1 : 0.3;
                                                                return (
                                                                    <Line
                                                                        key={estate}
                                                                        type="monotone"
                                                                        dataKey={estate}
                                                                        name={estate}
                                                                        stroke={COLORS[index % COLORS.length]}
                                                                        activeDot={{ r: 8 }}
                                                                        strokeWidth={1}
                                                                        strokeOpacity={opacity}
                                                                    />
                                                                );
                                                            })
                                                    }
                                                    {bjrChartData.length > 1 &&
                                                        Object.keys(bjrChartData[0])
                                                            .filter(key => key !== 'periode' && !key.endsWith('_trend'))
                                                            .map((estate, index) => {
                                                                const opacity = hoveringDataKey === undefined || hoveringDataKey === estate ? 0.6 : 0.2;
                                                                return (
                                                                    <Line
                                                                        key={`${estate}_trend`}
                                                                        type="monotone"
                                                                        dataKey={`${estate}_trend`}
                                                                        stroke={COLORS[index % COLORS.length]}
                                                                        strokeDasharray="5 5"
                                                                        strokeWidth={1}
                                                                        strokeOpacity={opacity}
                                                                        dot={false}
                                                                        activeDot={false}
                                                                        legendType="none"
                                                                    />
                                                                );
                                                            })
                                                    }
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Center flex={1}>
                                        <Text color="gray.500">Tidak ada data BJR</Text>
                                    </Center>
                                )}
                            </Box>
                        </Box>
                    </SimpleGrid>
                </Box>
            )}
        </Box>
    );
};

export default EstateDashboardComponent;